"use client";

import type { Project } from "@/lib/projects/types";
import { Routes } from "@/lib/routes";
import classNames from "classnames";
import Link from "next/link";
import { useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, type SwiperClass, SwiperSlide } from "swiper/react";
import { FormattedText } from "../FormattedText";
import { ProjectImage } from "../ProjectImage";
import styles from "./RecomendedSlider.module.css";

type Props = {
  projects: Project[];
};

export const RecomendedSlider = ({ projects }: Props) => {
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const handleSwiperInit = (swiper: SwiperClass) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  if (!projects) {
    return null;
  }

  return (
    <section className={styles.recomended}>
      <Swiper
        navigation={{
          nextEl: `.${styles["arrow-button-next"]}`,
          prevEl: `.${styles["arrow-button-prev"]}`,
        }}
        modules={[Navigation]}
        className={styles.swiper}
        onInit={handleSwiperInit}
        onSlideChange={handleSlideChange}
        breakpoints={{
          375: {
            slidesPerView: 1,
            spaceBetween: 10,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
        }}
      >
        {projects.map((p) => {
          return (
            <SwiperSlide key={p.slug} className={styles.slide}>
              <Link
                className={styles.card}
                href={`${Routes.PROJECTS}/${p.slug}`}
              >
                <div className={styles["image-wrapper"]}>
                  <ProjectImage
                    className={styles["card-image"]}
                    width={440}
                    height={250}
                    src={p.image}
                    alt={p.title}
                  />
                </div>
                <FormattedText>
                  <span className={styles["card-title"]}>{p.title}</span>
                </FormattedText>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <div className={styles["slider-controls"]}>
        <ArrowButton left={true} disabled={isBeginning} />
        <ArrowButton disabled={isEnd} />
      </div>
    </section>
  );
};

function ArrowButton({
  left,
  disabled,
}: {
  left?: boolean;
  disabled?: boolean;
}) {
  return (
    <svg
      role="img"
      aria-label={left ? "Кнопка предыдущий слайд" : "Кнопка следующий слайд"}
      className={classNames({
        [styles["arrow-button-prev"]]: left,
        [styles["arrow-button-next"]]: !left,
        [styles.disabled]: disabled,
      })}
      xmlns="http://www.w3.org/2000/svg"
      width="50"
      height="50"
      viewBox="0 0 50 50"
      fill="none"
      style={{ rotate: left ? "" : "-180deg" }}
    >
      <rect width="50" height="50" rx="25" fill="#F7FBFC" />
      <path
        d="M28.3777 13.0747L31.5371 16.2569L23.2165 24.6132L31.5371 32.9694L28.3777 36.1516L16.9217 24.6132L28.3777 13.0747Z"
        fill="#769FCD"
      />
    </svg>
  );
}
